import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingView } from 'day8-react';
import { PiInfo } from '@react-icons/all-files/pi/PiInfo';
import Destinations from '../components/planner/Destinations';
import { fetchDestinations, createNormalYachtPlan, fetchActivePlans } from '../actions/planner';
import history from '../history';
import { Button, toast, Hero, Card } from 'day8-ui';
import Spacer from '../components/Spacer';
import PlannerInfo from '../components/InfoDrawer/PlannerInfo/PlannerInfo';
import { useInfoDrawer } from '../context/InfoDrawerContext';
import PlansList from '../components/planner/PlansList';

const PlanStart = () => {
  const { openInfoDrawer } = useInfoDrawer();
  const [loading, setLoading] = useState(true);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const plans = useSelector(state => state.planner.activePlans) || [];
  const loggedUser = useSelector(state => state.user.loggedUser);
  const destinations = useSelector(state => state.destinations.items);
  const dispatch = useDispatch();
  useEffect(() => {
    handleInitialFetch();
  }, []);
  useEffect(() => {
    // if is coming back grom login modal (so it is still logging in to true) toggle plan based on remembered value from ref
    if (loggingIn && loggedUser) {
      handleDestinationSelect(selectedDestination);
    }
  }, [loggingIn, loggedUser]);
  const handleInitialFetch = useCallback(async () => {
    await dispatch(fetchDestinations());
    if (loggedUser) {
      setLoading(true);
      await dispatch(fetchActivePlans());
    }
    setLoading(false);
  });
  const handleDestinationSelect = useCallback(async destinationId => {
    try {
      if (!loggedUser) {
        setSelectedDestination(destinationId);
        setLoggingIn(true);
        history.replace({
          search: location.search,
          state: {
            showLoginModal: true,
            returnTo: { pathname: '/plan' },
            page: 'signup'
          }
        });
      } else {
        setLoggingIn(false);
        setCreatingPlan(true);
        const response = await dispatch(createNormalYachtPlan(destinationId, null, null, null, null));
        if (response.status !== 'success') {
          toast.error('Something went wrong. Please try again.');
        } else {
          history.push(`/plan/${response.data.booking.alphaId}`);
        }
      }
    } catch(e) {
      console.error(e);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setCreatingPlan(false);
    }
  });

  if (creatingPlan) {
    return <LoadingView title="Creating your plan" />;
  }
  if (loading) {
    return <LoadingView />;
  }
  return (
    <>
      <Hero
        heading='Yacht Week Planner'
        description="Save your favourite yachts, invite your friends, and create your perfect Yacht Week experience together."
        imageElement={<img src="https://wpassets.theyachtweek.com/wp-content/uploads/2025/08/Croatia-Angelo-Virag-July-16.jpg" alt="planner" />}
      />

      <Spacer />
      <div className="dui-container dui-mx-auto">
        <div className='dui-text-center'>
          <h3 className="dui-text-2xl lg:dui-text-6xl dui-font-heading !dui-font-normal dui-text-primary-950 dui-uppercase">We made it simple</h3>
          <p className='dui-mt-4'>
            <strong>Create a plan your way</strong> — either begin from scratch or save a yacht from the funnel.
          </p>
        </div>
        <Spacer />
        <Card className="!dui-p-0">
          <div className="dui-flex dui-flex-col md:dui-flex-row dui-w-full lg:dui-min-h-[400px]">
            <div className="dui-w-full lg:dui-w-1/2 dui-relative">
              <img
                alt='Where to go in Montenegro Yacht Week CREDIT Santino Martinez'
                className="dui-w-full dui-h-[400px] dui-min-h-full dui-object-cover"
                src='https://wpassets.theyachtweek.com/wp-content/uploads/2025/09/Croatia-Asa-Steinars.jpg'
              />
            </div>
            <div className="dui-w-full lg:dui-w-1/2 lg:dui-p-16 dui-p-4 dui-bg-secondary-100 dui-flex dui-flex-col dui-justify-center dui-text-primary-950">
              <h3 className="dui-font-subheading dui-text-2xl lg:dui-text-5xl dui-font-bold dui-mb-8 dui-text-primary-950 dui-uppercase">How Does It Work?</h3>
              <p className="dui-mb-8"><strong>From scratch:</strong> Pick a destination below, invite your Crew and vote on dates. Then explore yachts and plan costs together before booking.</p>
              <p className="dui-mb-8"><strong>From a saved yacht or package:</strong> Invite your crew directly from your plan. They can confirm whether they can join, review costs and add-ons and let you know when they are all ready.</p>
              <p className="dui-mb-8">Plan with your friends with no risk or deadlines. All crew details will automatically be added to your booking.</p>
              <div>
                <Button className='md:dui-mb-8' leftIcon={<PiInfo />} buttonType={"SECONDARY"} onClick={() => openInfoDrawer({ content: <PlannerInfo /> })}>Learn more</Button>
              </div>
            </div>
          </div>
        </Card>
        <Spacer />
      </div>
      {loggedUser && plans?.length > 0 && (
        <div className="dui-container dui-mx-auto">
          <div className='dui-text-center'>
            <h3 className="dui-text-2xl lg:dui-text-6xl dui-font-heading !dui-font-normal dui-text-primary-950 dui-uppercase">Existing plans</h3>
            <p className='dui-mt-4'>Jump back into your saved plans or explore a new destination below.</p>
          </div>
          {plans.filter(plan => plan.booker.id === loggedUser.id).length > 0 && (
            <>
              <Spacer />
              <PlansList plans={plans.filter(plan => plan.booker.id === loggedUser.id && plan.status === 'plan')} title="Saved plans" />
            </>
          )}
          {plans.filter(plan => plan.booker.id !== loggedUser.id).length > 0 && (
            <>
              <Spacer />
              <PlansList plans={plans.filter(plan => plan.booker.id !== loggedUser.id)} title="Invitations" />
            </>
          )}
        </div>
      )}

      <Spacer />

      {destinations && (
        <Destinations destinations={destinations} handleDestinationSelection={handleDestinationSelect} />
      )}
    </>
  );

};
export default PlanStart;